import localePt from "@angular/common/locales/pt";

import {
  Component,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  OnInit,
  computed,
  LOCALE_ID,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonModule, registerLocaleData } from "@angular/common";
import { MusicthonPlayerComponent } from "../alert/components/musicthon-player/musicthon-player.component";
import { MatIconModule } from "@angular/material/icon";
import { HttpClientModule } from "@angular/common/http";
import { MusicthonService } from "../../core/musicthon/musicthon.service";
import { MusicthonAckStatus } from "../../core/musicthon/dto/public-ack.request";
import { PublicMusicthonConfig } from "../../core/musicthon/model/musicthon-state.model";
import { SocketService } from "../../core/event/services/socket.service";
import { Subscription } from "rxjs";
import { DatadogService } from "../../core/observability/datadog.service";
import { trigger, transition, style, animate } from "@angular/animations";

registerLocaleData(localePt, "pt-BR");

interface MusicthonPlayEvent {
  entryId?: string;
  trackId?: string;
  provider?: string;
  reason?: string;
  trackTitle?: string | null;
  donorName?: string | null;
  amount?: number | null;
  wasPreempted?: boolean;
}

interface MusicthonStopEvent {
  entryId?: string | null;
  reason?: string;
}

interface MusicthonConfigEvent {
  featureEnabled?: boolean;
  enabled?: boolean;
  paused?: boolean;
  provider?: string | null;
  minAmount?: number | null;
  highestDonationWins?: boolean | null;
  beatAmount?: number | null;
  volume?: number | null;
  stealSfxUrl?: string | null;
  availableProviders?: string[];
  useMobileGain?: boolean;
}

@Component({
  standalone: true,
  selector: "app-musicthon",
  templateUrl: "./musicthon.component.html",
  styleUrls: ["./musicthon.component.scss"],
  imports: [
    CommonModule,
    MusicthonPlayerComponent,
    MatIconModule,
    HttpClientModule,
  ],
  providers: [{ provide: LOCALE_ID, useValue: "pt-BR" }],
  animations: [
    trigger("slideIn", [
      transition(":enter", [
        style({
          transform: "translateY(-20px) scale(0.95)",
          opacity: 0,
        }),
        animate(
          "400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          style({
            transform: "translateY(0) scale(1)",
            opacity: 1,
          })
        ),
      ]),
      transition(":leave", [
        animate(
          "300ms ease-out",
          style({
            transform: "translateY(-20px) scale(0.95)",
            opacity: 0,
          })
        ),
      ]),
    ]),
    trigger("stealFlash", [
      transition(":enter", [
        style({
          transform: "translateY(-16px) scale(0.96)",
          opacity: 0,
        }),
        animate(
          "220ms ease-out",
          style({
            transform: "translateY(0) scale(1)",
            opacity: 1,
          })
        ),
      ]),
      transition(":leave", [
        animate(
          "220ms ease-in",
          style({
            transform: "translateY(-10px) scale(0.98)",
            opacity: 0,
          })
        ),
      ]),
    ]),
  ],
})
export class MusicthonComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("player") player?: MusicthonPlayerComponent;

  streamerHash: string | null = null;
  streamerId: string | null = null;
  videoId: string | null = null;
  entryId: string | null = null;
  status = "Aguardando eventos...";
  lastError: string | null = null;
  private startedAckSent = false;
  private finalAckSent = false;
  private resyncing = false;
  private boundStreamerId: string | null = null;
  private eventSubs = new Subscription();
  private readonly socketHealthSubs: Subscription[] = [];
  private readonly subscriptions = new Subscription();
  private playbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly PLAYBACK_TIMEOUT_MS = 10000;
  private readonly MAX_PLAYBACK_RETRIES = 1;
  private playbackRetryCount = 0;
  readonly currentEntry = this.musicthonService.current();
  readonly publicConfig = this.musicthonService.publicConfig();
  readonly isEnabled = computed(() => this.publicConfig()?.enabled === true);
  readonly providerLabel = computed(() => {
    const provider = this.publicConfig()?.provider;
    if (provider === "YTMUSIC") return "YouTube";
    if (provider === "SPOTIFY") return "Spotify";
    return "";
  });
  readonly providerIcon = computed(() => {
    const provider = this.publicConfig()?.provider;
    if (provider === "YTMUSIC") {
      return "assets/icons/provider-youtube.svg";
    }
    if (provider === "SPOTIFY") {
      return "assets/icons/provider-spotify.svg";
    }
    return "";
  });
  playbackState:
    | "idle"
    | "loading"
    | "playing"
    | "paused"
    | "stopped"
    | "error" = "idle";
  private currentTitle: string | null = null;
  currentDonorName: string | null = null;
  currentAmount: number | null = null;
  stealCardDonorName: string | null = null;
  stealCardAmount: number | null = null;
  showStealCard = false;
  private stealCardTimeoutId: ReturnType<typeof setTimeout> | null = null;
  playbackVolume = 50;

  // Sistema de animação de batida musical
  beatIntensity: "low" | "medium" | "high" | "drop" = "medium";
  private beatAnimationId: number | null = null;
  private lastBeatTime = 0;
  private readonly BEAT_INTERVAL_MS = 500; // Batida a cada 500ms (120 BPM)
  private lastStopReason: string | null = null;
  private lastStopAt: number | null = null;
  private lastStoppedEntryId: string | null = null;
  private lastStoppedAmount: number | null = null;
  private readonly brFormatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
  showVideo = false;
  private readonly visibilityHandler = () => {
    if (document.visibilityState === "visible") {
      this.log("tab:visible");
      this.datadogService.trackAction("tab_visible", { type: "musicthon" });
      this.ensureConnectedAndResync("visibility");
      return;
    }
    this.log("tab:hidden");
    this.datadogService.trackAction("tab_hidden", { type: "musicthon" });
  };
  private readonly onlineHandler = () => {
    this.log("network:online");
    this.datadogService.trackAction("network_online", { type: "musicthon" });
    this.ensureConnectedAndResync("online");
  };
  private readonly offlineHandler = () => {
    this.log("network:offline");
    this.datadogService.logWarn("[Musicthon] Network offline", {
      streamerId: this.streamerId,
    });
    this.datadogService.trackAction("network_offline", { type: "musicthon" });
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly musicthonService: MusicthonService,
    private readonly socketService: SocketService,
    private readonly datadogService: DatadogService
  ) {
    const routeSub = this.route.params.subscribe((params) => {
      const nextHash = params["id"] || null;
      if (this.streamerHash && this.streamerHash !== nextHash) {
        this.resetStateForStreamerChange();
      }
      this.streamerHash = nextHash;
      this.log("route.params", { streamerHash: this.streamerHash });
      if (this.streamerHash) {
        this.connectToSocket(this.streamerHash);
      }
    });
    this.subscriptions.add(routeSub);
  }

  ngOnInit(): void {
    this.setupSocketHealthListeners();
    this.setupLifecycleListeners();
  }

  ngAfterViewInit(): void {
    this.startPlaybackIfNeeded();
  }

  onReady() {
    this.status = this.videoId ? "Player pronto" : "Pronto";
    this.log("player:ready", { entryId: this.entryId, videoId: this.videoId });
  }

  onPlaying() {
    this.clearPlaybackTimeout();
    this.status = this.videoId ? `Tocando ${this.videoId}` : "Tocando";
    this.playbackState = "playing";
    this.startBeatAnimation();
    this.log("player:playing", {
      entryId: this.entryId,
      videoId: this.videoId,
    });
    if (this.videoId && !this.startedAckSent) {
      this.sendAck("started", "playing");
      this.startedAckSent = true;
    }
  }

  onEnded() {
    this.status = "Finalizado";
    this.playbackState = "idle";
    this.stopBeatAnimation();
    this.log("player:ended", { entryId: this.entryId, videoId: this.videoId });
    this.clearPlaybackTimeout();
    // Resetar título para mostrar "Sem musica" quando acabar
    this.currentTitle = null;
    this.currentDonorName = null;
    this.currentAmount = null;
    if (!this.finalAckSent) {
      this.sendAck("done");
      this.finalAckSent = true;
    }
  }

  onError(err: string) {
    const errorInfo = this.describePlayerError(err);
    if (
      this.playbackRetryCount < this.MAX_PLAYBACK_RETRIES &&
      this.entryId &&
      this.videoId
    ) {
      this.playbackRetryCount += 1;
      this.lastError = null;
      this.status = "Tentando novamente...";
      this.playbackState = "loading";
      this.log("play:retry", {
        entryId: this.entryId,
        videoId: this.videoId,
        attempt: this.playbackRetryCount,
        err,
        reason: errorInfo.reason,
        message: errorInfo.message,
      });
      this.clearPlaybackTimeout();
      this.startedAckSent = false;
      this.finalAckSent = false;
      this.player?.play(this.videoId);
      this.schedulePlaybackTimeout({
        ignoreStartedAck: true,
        reason: "retry_timeout",
      });
      return;
    }

    this.lastError = errorInfo.message;
    this.status = "Erro ao reproduzir";
    this.playbackState = "error";
    this.stopBeatAnimation();
    this.log("player:error", {
      entryId: this.entryId,
      videoId: this.videoId,
      err,
      reason: errorInfo.reason,
      message: errorInfo.message,
    });
    this.clearPlaybackTimeout();
    if (!this.finalAckSent) {
      this.sendAck("failed", errorInfo.reason);
      this.finalAckSent = true;
    }
  }

  ngOnDestroy(): void {
    this.eventSubs.unsubscribe();
    this.subscriptions.unsubscribe();
    this.socketHealthSubs.forEach((sub) => sub.unsubscribe());
    this.teardownLifecycleListeners();
    this.clearPlaybackTimeout();
    this.clearStealCardTimeout();
    this.stopBeatAnimation();
  }

  private startPlaybackIfNeeded() {
    if (!this.videoId || !this.player) return;

    if (!this.isValidYouTubeId(this.videoId)) {
      this.lastError = "Invalid video id";
      this.status = "ID de vídeo inválido";
      this.log("play:invalid", { videoId: this.videoId });
      this.sendAck("failed", "invalid_video_id");
      this.finalAckSent = true;
      return;
    }

    this.status = `Tocando ${this.videoId}`;
    this.playbackState = "loading";
    this.log("play:start", { entryId: this.entryId, videoId: this.videoId });
    this.player.play(this.videoId);
  }

  private connectToSocket(streamerHash: string) {
    this.socketService.connect();
    const sub = this.socketService
      .emitWithAck("get_streamer_by_hash", { hash: streamerHash })
      .subscribe({
        next: (response: any) => {
          if (!response?.data?.id) {
            this.log("socket:missing_streamer", { response });
            return;
          }
          this.streamerId = response.data.id;
          this.log("socket:ready", {
            streamerId: this.streamerId,
            streamerHash,
          });
          if (this.streamerId) {
            this.datadogService.init(this.streamerId);
          }
          this.datadogService.setViewName(
            `musicthon/${this.streamerId || streamerHash}`
          );
          this.bindSocketEvents();
          this.resyncPlayback("initial_connect");
        },
        error: (err) => {
          this.log("socket:error", { err });
        },
      });
    this.subscriptions.add(sub);
  }

  private bindSocketEvents() {
    if (!this.streamerId) return;
    if (this.boundStreamerId === this.streamerId) return;
    if (this.boundStreamerId && this.boundStreamerId !== this.streamerId) {
      this.eventSubs.unsubscribe();
      this.eventSubs = new Subscription();
    }
    this.boundStreamerId = this.streamerId;

    this.eventSubs.add(
      this.socketService
        .onEvent<MusicthonPlayEvent>(
          `streamer:${this.streamerId}:musicthon_play`
        )
        .subscribe((payload) => this.handlePlayEvent(payload))
    );

    this.eventSubs.add(
      this.socketService
        .onEvent<MusicthonStopEvent>(
          `streamer:${this.streamerId}:musicthon_stop`
        )
        .subscribe((payload) => this.handleStopEvent(payload))
    );

    this.eventSubs.add(
      this.socketService
        .onEvent<MusicthonStopEvent>(
          `streamer:${this.streamerId}:musicthon_pause`
        )
        .subscribe((payload) => this.handlePauseEvent(payload))
    );

    this.eventSubs.add(
      this.socketService
        .onEvent<MusicthonStopEvent>(
          `streamer:${this.streamerId}:musicthon_resume`
        )
        .subscribe((payload) => this.handleResumeEvent(payload))
    );

    this.eventSubs.add(
      this.socketService
        .onEvent<MusicthonConfigEvent>(
          `streamer:${this.streamerId}:musicthon_config`
        )
        .subscribe((payload) => this.handleConfigEvent(payload))
    );
  }

  private setupSocketHealthListeners(): void {
    this.socketHealthSubs.push(
      this.socketService.onConnected$.subscribe(() => {
        this.log("socket:connected");
        this.datadogService.trackAction("socket_connected", {
          type: "musicthon",
        });
        if (this.streamerHash && !this.streamerId) {
          this.connectToSocket(this.streamerHash);
          return;
        }
        this.resyncPlayback("socket_connected");
      })
    );
    this.socketHealthSubs.push(
      this.socketService.onDisconnected$.subscribe((reason) => {
        this.log("socket:disconnected", { reason });
        this.datadogService.logWarn("[Musicthon] Socket disconnected", {
          reason,
          streamerId: this.streamerId,
        });
        this.datadogService.trackAction("socket_disconnected", {
          reason,
          type: "musicthon",
        });
      })
    );
  }

  private setupLifecycleListeners(): void {
    document.addEventListener("visibilitychange", this.visibilityHandler);
    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
  }

  private teardownLifecycleListeners(): void {
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    window.removeEventListener("online", this.onlineHandler);
    window.removeEventListener("offline", this.offlineHandler);
  }

  private ensureConnectedAndResync(reason: string) {
    if (!this.streamerHash) return;
    if (!this.socketService.isConnected()) {
      this.socketService.connect();
    }
    this.resyncPlayback(reason);
  }

  private async resyncPlayback(reason: string) {
    if (this.resyncing || !this.streamerHash) return;
    this.resyncing = true;
    try {
      this.log("resync:start", { reason, streamerHash: this.streamerHash });
      if (this.streamerId) {
        this.socketService
          .emitWithAck("musicthon_ready", {
            streamerId: this.streamerId,
            streamerHash: this.streamerHash,
            reason,
          })
          .subscribe({
            next: (resp) => this.log("musicthon_ready:ok", resp as any),
            error: (err) => this.log("musicthon_ready:error", { err }),
          });
      }
      const config = await this.musicthonService.loadPublicConfig(
        this.streamerHash
      );
      const isPaused = config?.paused === true;
      this.updatePlaybackConfig(config);
      this.applyEnabledState(config);
      await this.musicthonService.loadCurrent(this.streamerHash);
      const current = this.musicthonService.current()();
      if (current?.trackId) {
        if (isPaused) {
          this.applyPausedCurrent(current);
        } else {
          // Durante resync, forçar reload para garantir que player seja criado
          this.handlePlayEvent({
            entryId: current.id,
            trackId: current.trackId,
            trackTitle: current.trackTitle,
            donorName: current.donorName,
            amount: current.amount ?? null,
            reason,
          }, true); // forceReload = true
        }
      } else {
        if (this.videoId || this.entryId) {
          this.player?.stop();
          this.videoId = null;
          this.entryId = null;
          this.status = "Aguardando eventos...";
          this.playbackState = "idle";
        }
      }
    } finally {
      this.resyncing = false;
    }
  }

  private resetStateForStreamerChange(): void {
    this.eventSubs.unsubscribe();
    this.eventSubs = new Subscription();
    this.boundStreamerId = null;
    this.streamerId = null;
    this.videoId = null;
    this.entryId = null;
    this.startedAckSent = false;
    this.finalAckSent = false;
    this.status = "Aguardando eventos...";
    this.playbackState = "idle";
    this.lastError = null;
    this.currentTitle = null;
    this.currentDonorName = null;
    this.currentAmount = null;
    this.showStealCard = false;
    this.stealCardDonorName = null;
    this.stealCardAmount = null;
    this.clearStealCardTimeout();
    this.playbackVolume = 50;
    this.lastStopReason = null;
    this.lastStopAt = null;
    this.playbackRetryCount = 0;
  }

  private handlePlayEvent(payload: MusicthonPlayEvent, forceReload = false) {
    this.log("event:play", payload as Record<string, unknown>);
    const sanitized = this.sanitizeTrackId(payload.trackId ?? null);
    if (!sanitized) {
      this.lastError = "Invalid video id";
      this.status = "ID de vídeo inválido";
      this.entryId = payload.entryId ?? null;
      this.log("play:invalid", { raw: payload.trackId });
      this.sendAck("failed", "invalid_video_id");
      this.finalAckSent = true;
      return;
    }

    const incomingAmount =
      typeof payload.amount === "number" ? payload.amount : null;
    const nextEntryId = payload.entryId ?? null;
    
    // Backend informa se essa música roubou a anterior (via flag wasPreempted)
    const shouldShowStealCard = payload.wasPreempted === true;

    this.currentTitle = payload.trackTitle ?? this.currentTitle;
    this.currentDonorName = payload.donorName ?? null;
    this.currentAmount = incomingAmount;
    this.playbackRetryCount = 0;
    this.lastStopReason = null;
    this.lastStopAt = null;
    if (this.entryId && this.entryId !== nextEntryId) {
      this.startedAckSent = false;
      this.finalAckSent = false;
      this.clearPlaybackTimeout();
    }

    // Se mesma música e não é forceReload (resync), pula
    if (this.entryId === nextEntryId && this.videoId === sanitized && !forceReload) {
      return;
    }

    this.entryId = nextEntryId;
    this.videoId = sanitized;
    this.startedAckSent = false;
    this.finalAckSent = false;
    this.playbackRetryCount = 0;
    this.status = "Carregando vídeo...";
    this.playbackState = "loading";
    this.log("play:requested", {
      entryId: this.entryId,
      videoId: this.videoId,
    });
    if (shouldShowStealCard) {
      this.triggerStealCard({
        donorName: payload.donorName ?? null,
        amount: incomingAmount,
      });
    }
    this.startPlaybackIfNeeded();
    this.schedulePlaybackTimeout();
  }

  private handleStopEvent(payload: MusicthonStopEvent) {
    this.log("event:stop", payload as Record<string, unknown>);
    if (payload?.entryId && this.entryId && payload.entryId !== this.entryId) {
      return;
    }
    if (payload?.reason === "preempted") {
      this.lastStoppedEntryId = this.entryId;
      this.lastStoppedAmount = this.currentAmount;
    } else {
      this.lastStoppedEntryId = null;
      this.lastStoppedAmount = null;
    }
    this.finalAckSent = true;
    this.startedAckSent = true;
    this.clearPlaybackTimeout();
    this.player?.stop();
    this.videoId = null;
    this.entryId = null;
    this.status = "Parado";
    this.playbackState = "stopped";
    this.lastStopReason = payload?.reason ?? null;
    this.lastStopAt = Date.now();
    this.currentTitle = null;
    this.currentDonorName = null;
    this.currentAmount = null;
    this.playbackRetryCount = 0;
  }

  private handlePauseEvent(payload: MusicthonStopEvent) {
    this.log("event:pause", payload as Record<string, unknown>);
    if (payload?.entryId && this.entryId && payload.entryId !== this.entryId) {
      return;
    }
    this.clearPlaybackTimeout();
    this.player?.pause();
    this.status = "Pausado";
    this.playbackState = "paused";
  }

  private handleResumeEvent(payload: MusicthonStopEvent) {
    this.log("event:resume", payload as Record<string, unknown>);
    if (payload?.entryId && this.entryId && payload.entryId !== this.entryId) {
      return;
    }
    
    // Se player não existe mas tem videoId, precisa carregar primeiro
    if (!this.player && this.videoId) {
      this.log("event:resume:no_player", { videoId: this.videoId, entryId: this.entryId });
      this.startPlaybackIfNeeded();
    } else {
      this.player?.resume();
    }
    
    this.status = this.videoId ? `Retomando ${this.videoId}` : "Retomando";
    this.playbackState = "playing";
    
    // Não agendar timeout se for resume após alerta (Moblin workaround)
    // Nesses casos a música já está tocando, só estava mutada
    if (payload?.reason === 'alert_finished') {
      this.log("event:resume:alert_finished", { entryId: this.entryId, skipTimeout: true });
      return;
    }
    
    this.schedulePlaybackTimeout({
      ignoreStartedAck: true,
      reason: "resume_timeout",
    });
  }

  private handleConfigEvent(payload: MusicthonConfigEvent) {
    this.log("event:config", payload as Record<string, unknown>);
    if (payload && typeof payload.enabled === "boolean") {
      const current = this.publicConfig();
      this.musicthonService.setPublicConfig({
        featureEnabled: payload.featureEnabled ?? current?.featureEnabled,
        enabled: payload.enabled,
        paused: payload.paused ?? current?.paused ?? false,
        provider: payload.provider ?? current?.provider ?? null,
        minAmount: payload.minAmount ?? current?.minAmount ?? null,
        highestDonationWins:
          payload.highestDonationWins ?? current?.highestDonationWins ?? null,
        beatAmount: payload.beatAmount ?? current?.beatAmount ?? null,
        volume: payload.volume ?? current?.volume ?? 70,
        stealSfxUrl: payload.stealSfxUrl ?? current?.stealSfxUrl ?? null,
        availableProviders:
          payload.availableProviders ?? current?.availableProviders ?? [],
        useMobileGain: payload.useMobileGain ?? current?.useMobileGain ?? false,
      });
      this.updatePlaybackConfig(this.publicConfig());
      this.applyEnabledState(this.publicConfig());
    }
  }

  private updatePlaybackConfig(config?: PublicMusicthonConfig | null) {
    const volume =
      typeof config?.volume === "number" && !Number.isNaN(config.volume)
        ? Math.max(0, Math.min(100, config.volume))
        : 50;
    this.playbackVolume = volume;
  }

  private triggerStealCard(payload: {
    donorName?: string | null;
    amount?: number | null;
  }) {
    this.stealCardDonorName = payload.donorName ?? null;
    this.stealCardAmount =
      typeof payload.amount === "number" ? payload.amount : null;
    this.showStealCard = true;
    this.clearStealCardTimeout();
    // Mostrar card por 5 segundos
    this.stealCardTimeoutId = setTimeout(() => {
      this.showStealCard = false;
    }, 5000);
  }

  private clearStealCardTimeout() {
    if (this.stealCardTimeoutId) {
      clearTimeout(this.stealCardTimeoutId);
      this.stealCardTimeoutId = null;
    }
  }

  private isValidYouTubeId(id: string | null): boolean {
    if (!id) return false;
    return /^[A-Za-z0-9_-]{11}$/.test(id);
  }

  private sanitizeTrackId(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (this.isValidYouTubeId(trimmed)) return trimmed;
    const watch = /[?&]v=([^&#]+)/.exec(trimmed);
    if (watch?.[1] && this.isValidYouTubeId(watch[1])) return watch[1];
    const short = /youtu\.be\/([^?&#/]+)/.exec(trimmed);
    if (short?.[1] && this.isValidYouTubeId(short[1])) return short[1];
    const shorts = /youtube\.com\/shorts\/([^?&#/]+)/.exec(trimmed);
    if (shorts?.[1] && this.isValidYouTubeId(shorts[1])) return shorts[1];
    return null;
  }

  private sendAck(status: MusicthonAckStatus, reason?: string) {
    if (!this.streamerHash || !this.entryId) return;
    this.log("ack:send", {
      status,
      reason,
      entryId: this.entryId,
      streamerId: this.streamerHash,
    });
    this.musicthonService
      .publicAck({
        streamerHash: this.streamerHash,
        entryId: this.entryId,
        status,
        reason,
      })
      .then(() => {
        this.log("ack:ok", { entryId: this.entryId, status });
      })
      .catch((err) => {
        this.log("ack:error", { entryId: this.entryId, status, err });
      });
  }

  private schedulePlaybackTimeout(options?: {
    ignoreStartedAck?: boolean;
    reason?: string;
  }) {
    this.clearPlaybackTimeout();
    this.playbackTimeoutId = setTimeout(() => {
      if (
        (!options?.ignoreStartedAck && this.startedAckSent) ||
        this.finalAckSent ||
        !this.entryId
      ) {
        return;
      }
      this.lastError = "Playback timeout";
      this.status = "Erro ao reproduzir";
      this.log("play:timeout", {
        entryId: this.entryId,
        videoId: this.videoId,
      });
      this.logAutoplayBlocked(options?.reason ?? "playback_timeout");
      this.sendAck("failed", options?.reason ?? "playback_timeout");
      this.finalAckSent = true;
    }, this.PLAYBACK_TIMEOUT_MS);
  }

  private clearPlaybackTimeout() {
    if (this.playbackTimeoutId) {
      clearTimeout(this.playbackTimeoutId);
      this.playbackTimeoutId = null;
    }
  }

  private log(event: string, payload?: Record<string, unknown>) {
    if (
      event.startsWith("socket:disconnected") ||
      event.startsWith("network:")
    ) {
      this.datadogService.logWarn(`[Musicthon] ${event}`, payload ?? {});
      return;
    }
    if (event.startsWith("player:error") || event.startsWith("ack:error")) {
      this.datadogService.logError(`[Musicthon] ${event}`, payload ?? {});
      return;
    }
    this.datadogService.logInfo(`[Musicthon] ${event}`, payload ?? {});
  }

  private logAutoplayBlocked(reason: string) {
    this.log("autoplay:blocked", {
      entryId: this.entryId,
      videoId: this.videoId,
      reason,
      playbackState: this.playbackState,
    });
    this.datadogService.trackAction("autoplay_blocked", {
      type: "musicthon",
      reason,
      entryId: this.entryId,
    });
  }

  private describePlayerError(err: string): {
    code?: number;
    reason: string;
    message: string;
  } {
    const code = Number(err);
    if (!Number.isNaN(code)) {
      switch (code) {
        case 2:
          return {
            code,
            reason: "invalid_parameter",
            message: "Parâmetro inválido no player do YouTube",
          };
        case 5:
          return {
            code,
            reason: "html5_error",
            message: "Erro do player HTML5 do YouTube",
          };
        case 100:
          return {
            code,
            reason: "video_not_found",
            message: "Vídeo não encontrado ou removido",
          };
        case 101:
        case 150:
          return {
            code,
            reason: "embed_not_allowed",
            message: "Vídeo não permite reprodução incorporada",
          };
        default:
          return {
            code,
            reason: `youtube_error_${code}`,
            message: `Erro do YouTube (${code})`,
          };
      }
    }
    if (!err || err === "unknown_error") {
      return {
        reason: "unknown_error",
        message: "Erro desconhecido do player",
      };
    }
    return {
      reason: err,
      message: `Erro do player: ${err}`,
    };
  }

  formatEntry(
    entry?: {
      trackTitle?: string | null;
      trackId?: string | null;
      id?: string | null;
    } | null
  ) {
    if (!entry) return "Sem musica";
    const label = entry.trackTitle || entry.trackId || entry.id || "";
    return label || "Sem musica";
  }

  formatCurrentTitle() {
    if (this.currentTitle) {
      return this.currentTitle;
    }
    return this.formatEntry(this.currentEntry());
  }

  formatDonorLine() {
    if (!this.currentDonorName && this.currentAmount == null) {
      return "";
    }
    const parts: string[] = [];
    if (this.currentDonorName) {
      parts.push(this.currentDonorName);
    }
    if (this.currentAmount != null) {
      parts.push(this.brFormatter.format(this.currentAmount));
    }
    return parts.join(" • ");
  }

  formatAmount(amount: number): string {
    return this.brFormatter.format(amount);
  }

  private applyEnabledState(config?: PublicMusicthonConfig | null) {
    if (!config?.enabled) {
      if (this.videoId || this.entryId) {
        this.handleStopEvent({ entryId: this.entryId, reason: "disabled" });
      }
      this.playbackState = "idle";
      this.currentTitle = null;
      this.currentDonorName = null;
      this.currentAmount = null;
      this.showStealCard = false;
      this.clearStealCardTimeout();
      this.playbackRetryCount = 0;
      return;
    }

    if (config.paused) {
      this.clearPlaybackTimeout();
      this.player?.pause();
      this.playbackState = "paused";
      this.status = "Pausado";
      return;
    }

    if (this.entryId || this.videoId) {
      this.playbackState = "playing";
    } else {
      this.playbackState = "idle";
    }
  }

  private applyPausedCurrent(entry: {
    id?: string;
    trackId?: string | null;
    trackTitle?: string | null;
    donorName?: string | null;
    amount?: number | null;
  }) {
    const sanitized = this.sanitizeTrackId(entry.trackId ?? null);
    this.entryId = entry.id ?? null;
    this.videoId = sanitized;
    this.startedAckSent = false;
    this.finalAckSent = false;
    this.clearPlaybackTimeout();
    this.playbackState = "paused";
    this.status = "Pausado";
    this.currentTitle = entry.trackTitle ?? null;
    this.currentDonorName = entry.donorName ?? null;
    this.currentAmount = entry.amount ?? null;
    this.playbackRetryCount = 0;
  }

  /**
   * Inicia a animação de batida musical com variação inteligente
   * Simula diferentes intensidades e drops periódicos para parecer natural
   */
  private startBeatAnimation(): void {
    if (this.beatAnimationId !== null) {
      return; // Já está rodando
    }

    this.lastBeatTime = Date.now();
    this.animateBeat();
  }

  /**
   * Para a animação de batida e reseta para estado padrão
   */
  private stopBeatAnimation(): void {
    if (this.beatAnimationId !== null) {
      cancelAnimationFrame(this.beatAnimationId);
      this.beatAnimationId = null;
    }
    this.beatIntensity = "medium";
  }

  /**
   * Loop de animação que varia a intensidade baseada em padrões musicais
   */
  private animateBeat(): void {
    const now = Date.now();
    const elapsed = now - this.lastBeatTime;

    // Atualiza batida a cada intervalo
    if (elapsed >= this.BEAT_INTERVAL_MS) {
      this.updateBeatIntensity();
      this.lastBeatTime = now;
    }

    // Continua o loop se ainda está tocando
    if (this.playbackState === "playing") {
      this.beatAnimationId = requestAnimationFrame(() => this.animateBeat());
    } else {
      this.beatAnimationId = null;
    }
  }

  /**
   * Atualiza a intensidade da batida com variação inteligente
   * - 60% chance de intensidade média
   * - 25% chance de intensidade baixa
   * - 10% chance de intensidade alta
   * - 5% chance de drop (momento de impacto)
   */
  private updateBeatIntensity(): void {
    const random = Math.random();

    if (random < 0.05) {
      // 5% - Drop (momento épico)
      this.beatIntensity = "drop";
    } else if (random < 0.15) {
      // 10% - Alta intensidade
      this.beatIntensity = "high";
    } else if (random < 0.4) {
      // 25% - Baixa intensidade
      this.beatIntensity = "low";
    } else {
      // 60% - Intensidade média (mais comum)
      this.beatIntensity = "medium";
    }
  }
}
